/**
 * CompaniesList Component
 *
 * Displays a grid of company cards.
 * Click a company to navigate to CompanyDetailPage.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { companiesApi } from '@/services/contactsApi';
import type { Company } from '@/services/contactsApi';

interface CompaniesListProps {
  onCountChange?: (count: number) => void;
}

export default function CompaniesList({ onCountChange }: CompaniesListProps) {
  const { t } = useTranslation('people');
  const navigate = useNavigate();

  // State
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await companiesApi.list();
      setCompanies(response.companies || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.failed_to_load', { type: 'companies' }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Notify parent of count changes
  useEffect(() => {
    if (onCountChange) {
      onCountChange(companies.length);
    }
  }, [companies.length, onCountChange]);

  const handleCompanyClick = (companyId: string) => {
    navigate(`/people/companies/${companyId}`);
  };

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => fetchCompanies()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('contacts_list.retry')}
        </button>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Empty state
  if (companies.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg
          className="w-12 h-12 mx-auto text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <p className="font-medium">{t('companies_list.empty_state.title')}</p>
        <p className="text-sm mt-1">{t('companies_list.empty_state.description')}</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {companies.map((company) => (
        <div
          key={company.id}
          onClick={() => handleCompanyClick(company.id)}
          className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 font-semibold">
              {company.logo_url ? (
                <img
                  src={company.logo_url}
                  alt=""
                  className="w-full h-full rounded-lg object-cover"
                />
              ) : (
                company.name[0].toUpperCase()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">{company.name}</h3>
              {company.industry && (
                <p className="text-sm text-gray-500">{company.industry}</p>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-gray-500">
              {company.contact_count === 1 ? t('companies_list.contact_count', { count: company.contact_count }) : t('companies_list.contact_count_plural', { count: company.contact_count })}
            </span>
            {company.website && (
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {t('companies_list.website')}
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
